import { h, Component } from 'preact';
import { Text } from 'preact-i18n';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Home position for a watch: click the map or use the browser position; optionally
// save it on the profile (POST /api/nsfcompetitionwatches/home). Writes the watch's
// homeLat/homeLng through the store's standard saveField.
const NORWAY_CENTER = [64.5, 12.5];
const TILE_URL = 'https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png';

function ClickCatcher({ onClick }) {
    useMapEvents({
        click(e) { onClick(e.latlng.lat, e.latlng.lng); },
    });
    return null;
}

function homeIcon() {
    return L.divIcon({
        html: '<div style="background:#0d6efd;color:#fff;width:24px;height:24px;border-radius:50%;display:flex;'
            + 'align-items:center;justify-content:center;font-size:14px;box-shadow:0 1px 4px rgba(0,0,0,.4)">⌂</div>',
        className: 'nsf-home-marker',
        iconSize: [24, 24],
        iconAnchor: [12, 12],
    });
}

class HomePicker extends Component {
    constructor(props) {
        super(props);
        this.state = { saveAsHome: false, positionError: false, homeSaved: false };
    }

    setPosition = async (lat, lng) => {
        const { stores, watch } = this.props;
        const { nsfCompetitionWatchStore } = stores;
        const roundedLat = Math.round(lat * 100000) / 100000;
        const roundedLng = Math.round(lng * 100000) / 100000;
        await nsfCompetitionWatchStore.saveHomePosition(watch.id, roundedLat, roundedLng);
        if (this.state.saveAsHome) {
            await this.saveOnProfile(roundedLat, roundedLng);
        }
    }

    saveOnProfile = async (lat, lng) => {
        const { stores, watch } = this.props;
        const { nsfCompetitionWatchStore } = stores;
        const response = await nsfCompetitionWatchStore.saveHome({ lat, lng, label: watch.homeLabel || '' });
        this.setState({ homeSaved: !!(response && response.status < 300) });
    }

    useMyPosition = () => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            this.setState({ positionError: true });
            return;
        }
        this.setState({ positionError: false });
        navigator.geolocation.getCurrentPosition(
            position => this.setPosition(position.coords.latitude, position.coords.longitude),
            () => this.setState({ positionError: true }),
            { enableHighAccuracy: true, timeout: 10000 },
        );
    }

    toggleSaveAsHome = async (e) => {
        const saveAsHome = !!e.target.checked;
        this.setState({ saveAsHome });
        const { watch } = this.props;
        if (saveAsHome && Number.isFinite(watch.homeLat) && Number.isFinite(watch.homeLng)) {
            await this.saveOnProfile(watch.homeLat, watch.homeLng);
        }
    }

    render() {
        const { watch = {}, stores } = this.props;
        const { positionError, saveAsHome, homeSaved } = this.state;
        const hasHome = Number.isFinite(watch.homeLat) && Number.isFinite(watch.homeLng);
        const { home } = stores.nsfCompetitionWatchStore;
        const center = hasHome ? [watch.homeLat, watch.homeLng] : (home ? [home.lat, home.lng] : NORWAY_CENTER);
        return (
            <div>
                <div class='small text-body-secondary mb-1'>
                    <Text id='terminliste.homeHelp'>Click the map to set your home position, or use your position.</Text>
                </div>
                <div style={{ height: '240px', width: '100%' }} class='rounded overflow-hidden border mb-2'>
                    <MapContainer center={center} zoom={hasHome ? 10 : 5} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }}>
                        <TileLayer attribution='&copy; <a href="https://statkart.no">Kartverket</a>' url={TILE_URL} />
                        <ClickCatcher onClick={this.setPosition} />
                        {hasHome && <Marker position={[watch.homeLat, watch.homeLng]} icon={homeIcon()} />}
                    </MapContainer>
                </div>
                <div class='d-flex flex-wrap align-items-center gap-2'>
                    <button type='button' class='btn btn-sm btn-outline-primary' onClick={this.useMyPosition}>
                        <i class='fa-duotone fa-location-crosshairs me-1' />
                        <Text id='terminliste.useMyPosition'>Use my position</Text>
                    </button>
                    {hasHome && <span class='small text-body-secondary'>{watch.homeLat}, {watch.homeLng}</span>}
                    <div class='form-check form-switch mb-0 ms-auto'>
                        <input class='form-check-input' type='checkbox' id={`save-as-home-${watch.id}`} checked={saveAsHome} onChange={this.toggleSaveAsHome} />
                        <label class='form-check-label small' for={`save-as-home-${watch.id}`}>
                            <Text id='terminliste.saveAsHome'>Save as home on my profile</Text>
                        </label>
                    </div>
                </div>
                {positionError && <div class='small text-danger mt-1'><Text id='terminliste.positionFailed'>Could not find your position.</Text></div>}
                {homeSaved && <div class='small text-success mt-1'><Text id='terminliste.homeSaved'>Home position saved.</Text></div>}
            </div>
        );
    }
}

export default HomePicker;
